import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Code, CopyButton } from 'dashboard/components/common';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';

const SCOPES = [
  { name: 'read:orders',    desc: 'List + fetch orders' },
  { name: 'read:products',  desc: 'List + fetch products' },
  { name: 'read:customers', desc: 'List storefront customers' },
  { name: 'read:reports',   desc: 'Read the brand overview report' },
];

/**
 * Two-step dialog: pick name + scopes → API call → show the secret
 * exactly once. After the owner clicks "I've saved it" the parent
 * reloads its list and the dialog closes.
 */
export default function CreateApiKeyDialog({ open, onClose, brandId, onSaved }) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (open) {
      setName(''); setScopes([]); setCreated(null);
    }
  }, [open]);

  const toggle = (s) => {
    setScopes((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await API.createBrandApiKey({
        brand_id: brandId,
        name: name.trim(),
        scopes,
      });
      setCreated(res);
      toast.success('Key created');
    } catch (err) {
      toast.error(err?.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const dismiss = () => { onSaved && onSaved(); onClose(); };

  return (
    <Dialog
      open={open}
      onClose={dismiss}
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle>{created ? 'Your new API key' : 'New API key'}</DialogTitle>
      <DialogContent dividers>
        {created ? (
          <Box>
            <Typography variant='body2' color='text.secondary' mb='8px'>
              Copy this now — it won't be shown again. Use it as
              {' '}<Code>Authorization: Bearer …</Code> on requests to <Code>/api/v1/…</Code>.
            </Typography>
            <Code sx={{ display: 'block', p: '10px', borderRadius: '8px' }}>
              {created.key}
            </Code>
            <CopyButton value={created.key} label='Copy key' sx={{ mt: '12px' }} />
          </Box>
        ) : (
          <Stack spacing='14px' sx={{ pt: '4px' }}>
            <TextField
              fullWidth size='small'
              label='Name (so you can spot it later)'
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='Acme Zapier integration'
            />
            <Box>
              <Typography variant='body2' fontWeight='600' mb='4px'>Scopes</Typography>
              <Typography variant='caption' color='text.secondary' display='block' mb='6px'>
                Leave all unchecked to grant every read scope.
              </Typography>
              <Stack spacing='6px'>
                {SCOPES.map((s) => (
                  <FormControlLabel
                    key={s.name}
                    control={
                      <Switch
                        size='small'
                        checked={scopes.includes(s.name)}
                        onChange={() => toggle(s.name)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant='body2' fontWeight='600'>{s.name}</Typography>
                        <Typography variant='caption' color='text.secondary'>{s.desc}</Typography>
                      </Box>
                    }
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {created ? (
          <Button variant='contained' onClick={dismiss}>I've saved it</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant='contained' onClick={save} disabled={saving}>
              {saving ? 'Creating…' : 'Create key'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
